import React from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { Paginator } from 'primereact/paginator';
import { groupLobOptions, isCertificateLobClass, LobOption } from './lobOptions';

// Server-side search + traditional prev/next paging. AMS caps every LineOfBusinessList
// response at 20 rows regardless of $top, so the page size matches that cap and the
// search term is sent to AMS (which filters before capping). Nothing is loaded in bulk or
// filtered in the browser, so the component keeps working if this list ever grows from
// high-level lines of business to thousands of specific NAICS entries.
const PAGE_SIZE = 20;

type LobPage = { items: LobOption[]; total: number };

async function fetchLobPage(query: string, page: number): Promise<LobPage> {
  const params = new URLSearchParams({ top: String(PAGE_SIZE), skip: String(page * PAGE_SIZE) });
  if (query) params.set('search', query);
  const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/line-of-business?${params}`, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
      NCToken: localStorage.getItem('NCToken') || '',
    },
  });
  if (!res.ok) return { items: [], total: 0 };
  const data = await res.json();
  const raw: any[] = data.value ?? [];
  const items = raw
    .map((item: any) => ({
      name: (item.name ?? '') as string,
      id: (item.databaseId ?? '') as string,
      cls: (item.class ?? '') as string,
    }))
    .filter(o => o.name);
  const total = Number(data['@odata.count']);
  return { items, total: Number.isFinite(total) ? total : items.length };
}

interface LobSearchDropdownProps {
  value: string;
  onChange: (name: string) => void;
  /** Called with the LOB databaseId when an option is selected from the list, or null when text is typed directly. */
  onIdChange?: (id: string | null) => void;
  className?: string;
  disabled?: boolean;
}

const LobSearchDropdown: React.FC<LobSearchDropdownProps> = ({
  value,
  onChange,
  onIdChange,
  className,
  disabled,
}) => {
  const [suggestions, setSuggestions] = React.useState<LobOption[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  // Ignore responses from superseded requests (fast typing, rapid prev/next clicks).
  const requestSeq = React.useRef(0);

  const load = React.useCallback(async (nextQuery: string, nextPage: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    const result = await fetchLobPage(nextQuery, nextPage);
    if (seq !== requestSeq.current) return;
    setSuggestions(groupLobOptions(result.items));
    setTotal(result.total);
    setLoading(false);
  }, []);

  // Fired by typing (debounced via `delay`) and by the dropdown button (empty query).
  const handleComplete = (e: { query: string }) => {
    const q = (e.query ?? '').trim();
    setQuery(q);
    setPage(0);
    load(q, 0);
  };

  const handleChange = (e: { value: LobOption | string | null }) => {
    if (e.value && typeof e.value === 'object') {
      onChange(e.value.name);
      onIdChange?.(e.value.id || null);
    } else {
      onChange((e.value as string) ?? '');
      onIdChange?.(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const footer = total > 0 ? (
    <div
      className="lobSearch__footer"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', borderTop: '1px solid #e5e7eb' }}
      // Keep the panel open when the pager is clicked — otherwise the input blur closes it.
      onMouseDown={(ev) => ev.preventDefault()}
    >
      <small style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
        {total} result{total === 1 ? '' : 's'}
      </small>
      <Paginator
        first={page * PAGE_SIZE}
        rows={PAGE_SIZE}
        totalRecords={total}
        onPageChange={(ev) => { setPage(ev.page); load(query, ev.page); }}
        template="PrevPageLink CurrentPageReport NextPageLink"
        currentPageReportTemplate={`Page {currentPage} of ${totalPages}`}
        style={{ padding: 0, background: 'transparent', border: 'none' }}
      />
    </div>
  ) : null;

  return (
    <AutoComplete
      value={value}
      suggestions={suggestions}
      field="name"
      // Show the AMS class on every row and mute the ones that never map to a certificate
      // coverage row, so "Umbrella Coverage" (Life-Health) no longer passes for "Commercial Umbrella".
      itemTemplate={(o: LobOption) => {
        const muted = !isCertificateLobClass(o.cls);
        return (
          <span style={{ color: muted ? '#9ca3af' : undefined }}>
            {o.name}
            <small style={{ marginLeft: 8, color: muted ? '#9ca3af' : '#6b7280' }}>{o.cls || 'Unclassified'}</small>
          </span>
        );
      }}
      completeMethod={handleComplete}
      onChange={handleChange}
      delay={300}
      minLength={0}
      dropdown
      dropdownMode="blank"
      forceSelection={false}
      disabled={disabled}
      placeholder="Search lines of business..."
      className={className}
      inputStyle={{ width: '100%' }}
      style={{ width: '100%' }}
      panelStyle={{ width: '480px', maxWidth: '480px' }}
      panelFooterTemplate={() => footer}
      emptyMessage={loading ? 'Searching...' : 'No lines of business found'}
      showEmptyMessage
    />
  );
};

export default LobSearchDropdown;
